import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PublicAPI } from "../api";
import { useAuth } from "../context/AuthContext";

const validatePassword = (password) => ({
  length: password.length >= 8,
  upper: /[A-Z]/.test(password),
  lower: /[a-z]/.test(password),
  number: /[0-9]/.test(password),
  special: /[^A-Za-z0-9]/.test(password),
});

export default function Register() {
  const { isAuthenticated, isValidating } = useAuth();
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    confirm_password: "",
    is_mentor: null,
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    if (!isValidating && isAuthenticated) {
      navigate("/");
    }
  }, [isAuthenticated, isValidating, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    if (fieldErrors[name]) {
      setFieldErrors({ ...fieldErrors, [name]: undefined });
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    if (form.is_mentor === null) {
      setFieldErrors({ role: "Please choose how you want to use the platform" });
      return;
    }

    if (!form.password || !form.email || !form.first_name || !form.last_name) {
      setFieldErrors({ ...fieldErrors, password: "All fields are required!" });
      return;
    }

    if (form.password !== form.confirm_password) return;

    const rules = validatePassword(form.password);
    if (!Object.values(rules).every(Boolean)) {
      setFieldErrors({ password: "Password does not meet requirements" });
      return;
    }

    try {
      await PublicAPI.post("auth/register/", {
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        password: form.password,
        is_mentor: form.is_mentor,
      });

      navigate("/login", {
        state: {
          success: "Account created! Please check your email to verify your address before logging in.",
        },
      });
    } catch (err) {
      if (err.response?.data) {
        setFieldErrors(err.response.data);
      }
    }
  };

  const inputClass = "w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#282E6A] focus:border-[#282E6A] focus:outline-none transition";
  const emailError = Array.isArray(fieldErrors.email) ? fieldErrors.email[0] : fieldErrors.email;
  const passwordError = Array.isArray(fieldErrors.password) ? fieldErrors.password[0] : fieldErrors.password;

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg bg-white border border-gray-200 rounded-2xl shadow-sm p-8">

        {/* Brand mark */}
        <div className="flex justify-center mb-6">
          <img src="/logo.svg" alt="Hindsight" className="h-10 w-auto" />
        </div>

        <h2 className="text-2xl font-semibold text-center text-gray-900 mb-1">
          Create your account
        </h2>
        <p className="text-sm text-center text-gray-500 mb-7">
          Join thousands of students getting honest college guidance
        </p>

        <form onSubmit={handleRegister} className="space-y-5">

          {/* Role selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              How will you use Hindsight?
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setForm({ ...form, is_mentor: false })}
                className={`p-4 border-2 rounded-xl text-left transition ${
                  form.is_mentor === false
                    ? "border-[#282E6A] bg-[#eef0fa]"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <p className="font-semibold text-gray-900 text-sm">Student</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  High school student seeking college guidance
                </p>
              </button>

              <button
                type="button"
                onClick={() => setForm({ ...form, is_mentor: true })}
                className={`p-4 border-2 rounded-xl text-left transition ${
                  form.is_mentor === true
                    ? "border-[#282E6A] bg-[#eef0fa]"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <p className="font-semibold text-gray-900 text-sm">Mentor</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  College student or alum offering advice
                </p>
              </button>
            </div>
            {fieldErrors.role && (
              <p className="text-xs text-red-600 mt-1.5">{fieldErrors.role}</p>
            )}

            {form.is_mentor === true && (
              <div className="mt-3 flex gap-2.5 rounded-xl border border-[#dde0f7] bg-[#eef0fa] px-4 py-3">
                <svg className="h-4 w-4 text-[#282E6A] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
                </svg>
                <p className="text-xs text-[#282E6A] leading-relaxed">
                  <span className="font-semibold">Use your college email address</span> (e.g. <span className="font-medium">you@mit.edu</span>) to get verified faster. Verified mentors appear first in search results.
                </p>
              </div>
            )}
          </div>

          {/* Name row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="register-first-name" className="mb-1.5 block text-sm font-medium text-gray-700">
                First name
              </label>
              <input
                id="register-first-name"
                name="first_name"
                placeholder="First name"
                value={form.first_name}
                onChange={handleChange}
                className={inputClass}
                required
              />
            </div>
            <div>
              <label htmlFor="register-last-name" className="mb-1.5 block text-sm font-medium text-gray-700">
                Last name
              </label>
              <input
                id="register-last-name"
                name="last_name"
                placeholder="Last name"
                value={form.last_name}
                onChange={handleChange}
                className={inputClass}
                required
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label htmlFor="register-email" className="mb-1.5 block text-sm font-medium text-gray-700">
              Email address
            </label>
            <input
              id="register-email"
              type="email"
              name="email"
              placeholder="Email address"
              value={form.email}
              onChange={handleChange}
              required
              className={inputClass}
              aria-invalid={!!emailError}
              aria-describedby={emailError ? "register-email-error" : undefined}
            />
            {emailError && (
              <p id="register-email-error" className="text-xs text-red-600 mt-1">{emailError}</p>
            )}
          </div>

          {/* Password */}
          <div>
            <label htmlFor="register-password" className="mb-1.5 block text-sm font-medium text-gray-700">
              Password
            </label>
            <input
              id="register-password"
              type="password"
              name="password"
              placeholder="Password"
              value={form.password}
              onChange={handleChange}
              required
              className={inputClass}
              aria-invalid={!!passwordError}
              aria-describedby={passwordError ? "register-password-error" : "register-password-help"}
            />
            <p id="register-password-help" className="mt-1.5 text-xs text-gray-400">
              Min. 8 characters with uppercase, lowercase, number, and special character
            </p>
          </div>

          {/* Confirm password */}
          <div>
            <label htmlFor="register-confirm-password" className="mb-1.5 block text-sm font-medium text-gray-700">
              Confirm password
            </label>
            <input
              id="register-confirm-password"
              type="password"
              name="confirm_password"
              placeholder="Confirm password"
              value={form.confirm_password}
              onChange={handleChange}
              required
              className={`${inputClass} ${
                form.confirm_password.length === 0
                  ? ""
                  : form.password === form.confirm_password
                  ? "border-green-500 focus:ring-green-500"
                  : "border-red-400 focus:ring-red-400"
              }`}
            />
            {form.confirm_password.length > 0 && (
              <p className={`mt-1 text-xs ${
                form.password === form.confirm_password ? "text-green-600" : "text-red-600"
              }`}>
                {form.password === form.confirm_password
                  ? "Passwords match"
                  : "Passwords do not match"}
              </p>
            )}
          </div>

          {passwordError && (
            <p id="register-password-error" className="text-xs text-red-600">{passwordError}</p>
          )}

          <button
            type="submit"
            className="w-full bg-[#282E6A] hover:bg-[#1e2458] text-white py-2.5 rounded-lg text-sm font-medium transition mt-1"
          >
            Create account
          </button>

          <p className="text-sm text-center text-gray-500">
            Already have an account?{" "}
            <Link to="/login" className="font-medium text-[#282E6A] hover:underline">
              Log in
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
